import type { TrackRecord } from "../types";
import { setDetailTitle } from "../meta";
import { escapeHtml } from "../util/html";
import { resolveAudioUrl } from "../util/audio";
import { getOrCreateAudioGraph } from "../util/audio-graph";
import { fetchJson } from "../util/fetch-json";
import type { MusicSceneHandle, VizStyle } from "../viz/music-scene";

const TRACKS_DATA_URL = "/data/tracks-local.json";
const STYLE_KEY = "sanoi-viz-style";

const VIZ_STYLE_OPTIONS: { id: VizStyle; label: string }[] = [
  { id: "orchestra", label: "管弦" },
  { id: "starfield", label: "星空" },
];

type TracksPayload = { tracks: TrackRecord[] };

function readSavedStyle(): VizStyle {
  const raw = sessionStorage.getItem(STYLE_KEY);
  if (raw === "starfield" || raw === "orchestra") return raw;
  // 旧版 classic 映射到管弦
  return "orchestra";
}

export function renderVisualizer(): string {
  return `
    <div id="viz-root" class="viz-page">
      <p class="section-sub viz-loading">正在准备粒子特效…</p>
    </div>
  `;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function shellHtml(t: TrackRecord, audioSrc: string, tracks: TrackRecord[], style: VizStyle): string {
  const options = tracks
    .filter((x) => x.audioFile)
    .map(
      (x) =>
        `<option value="${x.id}" ${x.id === t.id ? "selected" : ""}>${escapeHtml(x.composer)} · ${escapeHtml(x.title)}</option>`
    )
    .join("");
  const styleBtns = VIZ_STYLE_OPTIONS.map(
    (s) =>
      `<button type="button" class="viz-style-btn ${s.id === style ? "active" : ""}" data-style="${s.id}" aria-pressed="${s.id === style ? "true" : "false"}">${escapeHtml(s.label)}</button>`
  ).join("");

  return `
    <div class="viz-stage" id="viz-stage" aria-hidden="true"></div>
    <div class="viz-grain" aria-hidden="true"></div>
    <div class="viz-ui">
      <div class="viz-top">
        <a href="/music?track=${t.id}" class="viz-back">← 返回曲目</a>
        <div class="viz-top-tools">
          <div class="viz-style" role="group" aria-label="粒子风格">
            <span class="viz-picker-label">风格</span>
            ${styleBtns}
          </div>
          <label class="viz-picker">
            <span class="viz-picker-label">曲目</span>
            <select id="viz-track-select" aria-label="选择曲目">${options}</select>
          </label>
        </div>
      </div>
      <div class="viz-meta">
        <p class="viz-kicker">粒子特效 · ${style === "starfield" ? "星空" : "管弦"}</p>
        <h1 class="viz-title">${escapeHtml(t.title)}</h1>
        <p class="viz-sub">${escapeHtml(t.composer)}${t.era ? ` · ${escapeHtml(t.era)}` : ""}${t.year != null ? ` · ${t.year}` : ""}</p>
      </div>
      <div class="viz-controls">
        <button type="button" class="viz-play" id="viz-play" aria-label="播放">播放</button>
        <div class="viz-progress-wrap">
          <input type="range" id="viz-seek" class="viz-seek" min="0" max="1000" value="0" aria-label="进度" />
          <div class="viz-time">
            <span id="viz-cur">0:00</span>
            <span id="viz-dur">0:00</span>
          </div>
        </div>
      </div>
      <audio id="viz-audio" preload="metadata" src="${escapeHtml(audioSrc)}" crossorigin="anonymous"></audio>
    </div>
  `;
}

export function hydrateVisualizer(container: HTMLElement): void {
  void hydrateVisualizerAsync(container);
}

async function hydrateVisualizerAsync(container: HTMLElement): Promise<void> {
  const root = container.querySelector("#viz-root");
  if (!root) return;

  document.body.classList.add("sanoi-viz-mode");
  const cleanupBody = () => document.body.classList.remove("sanoi-viz-mode");

  try {
    const data = await fetchJson<TracksPayload>(TRACKS_DATA_URL, "曲库");
    if (!root.isConnected) {
      cleanupBody();
      return;
    }
    const tracks = (data.tracks ?? []).filter((t) => (t.audioFile || "").trim());
    if (!tracks.length) {
      root.innerHTML = `<p class="section-sub">暂无曲目。<a href="/music">返回名曲馆</a></p>`;
      cleanupBody();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const raw = params.get("track");
    const id = raw != null ? Number(raw) : NaN;
    const track = tracks.find((x) => x.id === id) ?? tracks[0]!;

    const src = resolveAudioUrl(track.audioFile!);
    const style = readSavedStyle();
    root.innerHTML = shellHtml(track, src, tracks, style);
    setDetailTitle(`${track.title} · 粒子特效`);
    wireSession(root as HTMLElement, tracks, style);
  } catch (e) {
    cleanupBody();
    if (root.isConnected) {
      const msg = e instanceof Error ? e.message : "无法加载";
      root.innerHTML = `<p class="section-sub">${escapeHtml(msg)}。<a href="/music">返回名曲馆</a></p>`;
    }
  }
}

function wireSession(root: HTMLElement, tracks: TrackRecord[], initialStyle: VizStyle): void {
  const stage = root.querySelector<HTMLElement>("#viz-stage");
  const audio = root.querySelector<HTMLAudioElement>("#viz-audio");
  const playBtn = root.querySelector<HTMLButtonElement>("#viz-play");
  const seek = root.querySelector<HTMLInputElement>("#viz-seek");
  const curEl = root.querySelector<HTMLElement>("#viz-cur");
  const durEl = root.querySelector<HTMLElement>("#viz-dur");
  const select = root.querySelector<HTMLSelectElement>("#viz-track-select");
  const kicker = root.querySelector<HTMLElement>(".viz-kicker");
  if (!stage || !audio || !playBtn || !seek || !curEl || !durEl) return;

  let scene: MusicSceneHandle | null = null;
  let seeking = false;
  let style: VizStyle = initialStyle;

  const syncStyleUi = () => {
    root.querySelectorAll<HTMLButtonElement>(".viz-style-btn").forEach((btn) => {
      const id = btn.getAttribute("data-style");
      const on = id === style;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    });
    if (kicker) kicker.textContent = `粒子特效 · ${style === "starfield" ? "星空" : "管弦"}`;
  };

  const ensureGraphAndScene = async () => {
    const graph = getOrCreateAudioGraph(audio);
    await graph.resume();
    if (!scene) {
      const { createMusicScene } = await import("../viz/music-scene");
      if (!root.isConnected) return;
      scene = createMusicScene({ container: stage, graph, style });
      scene.start();
    }
  };

  root.querySelectorAll<HTMLButtonElement>(".viz-style-btn").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const next = btn.getAttribute("data-style") as VizStyle | null;
      if (!next || next === style) return;
      style = next;
      sessionStorage.setItem(STYLE_KEY, style);
      syncStyleUi();
      if (scene) scene.setStyle(style);
      else {
        try {
          await ensureGraphAndScene();
        } catch {
          /* until play */
        }
      }
    });
  });

  const syncPlayUi = () => {
    const playing = !audio.paused;
    playBtn.textContent = playing ? "暂停" : "播放";
    playBtn.setAttribute("aria-label", playing ? "暂停" : "播放");
    playBtn.classList.toggle("is-playing", playing);
  };

  playBtn.addEventListener("click", async () => {
    try {
      await ensureGraphAndScene();
      if (audio.paused) await audio.play();
      else audio.pause();
    } catch {
      playBtn.textContent = "无法播放";
    }
  });

  audio.addEventListener("play", () => {
    void ensureGraphAndScene();
    syncPlayUi();
  });
  audio.addEventListener("pause", syncPlayUi);
  audio.addEventListener("ended", syncPlayUi);

  audio.addEventListener("loadedmetadata", () => {
    durEl.textContent = formatTime(audio.duration);
  });

  audio.addEventListener("timeupdate", () => {
    if (seeking || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    seek.value = String(Math.round((audio.currentTime / audio.duration) * 1000));
    curEl.textContent = formatTime(audio.currentTime);
    durEl.textContent = formatTime(audio.duration);
  });

  seek.addEventListener("pointerdown", () => {
    seeking = true;
  });
  seek.addEventListener("pointerup", () => {
    seeking = false;
  });
  seek.addEventListener("input", () => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const t = (Number(seek.value) / 1000) * audio.duration;
    audio.currentTime = t;
    curEl.textContent = formatTime(t);
  });

  select?.addEventListener("change", () => {
    const nextId = Number(select.value);
    const next = tracks.find((x) => x.id === nextId);
    if (!next?.audioFile) return;
    scene?.dispose();
    scene = null;
    const url = new URL(window.location.href);
    url.searchParams.set("track", String(next.id));
    window.history.replaceState({}, "", url.pathname + url.search);
    window.dispatchEvent(new PopStateEvent("popstate"));
  });

  const onResize = () => scene?.resize();
  window.addEventListener("resize", onResize, { passive: true });

  const observer = new MutationObserver(() => {
    if (!root.isConnected) {
      scene?.dispose();
      scene = null;
      window.removeEventListener("resize", onResize);
      document.body.classList.remove("sanoi-viz-mode");
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  syncPlayUi();
  if (Number.isFinite(audio.duration)) durEl.textContent = formatTime(audio.duration);
}
